import type { ContentShape, ContentStatus } from '../../types/content'
import { STATUS_TRANSITIONS, TRANSITION_CONTROL, type ContentTransition } from './content-status'

/** Every action a post or article offers, on the dock and on the calendar's right-click menu. */
export type PieceAction =
  | 'open'
  | 'edit'
  | 'schedule'
  | 'publishNow'
  | 'reschedule'
  | 'unschedule'
  | 'retryPublish'
  | 'duplicate'
  | 'remove'

/** The actions that run as a write and so carry an in-flight state. */
export type PieceWriteAction = Exclude<PieceAction, 'open' | 'edit'>

/** The write each {@link ContentTransition} runs. */
export const TRANSITION_ACTION: Record<ContentTransition, PieceWriteAction> = {
  schedule: 'schedule',
  publishNow: 'publishNow',
  reschedule: 'reschedule',
  unschedule: 'unschedule',
  retry: 'retryPublish',
}

/** What the action rules read off a post or article. */
export interface PieceActionSubject {
  readonly shape: ContentShape
  readonly status: ContentStatus
  /** Raldo is still writing the post. */
  readonly writing: boolean
}

/** What a piece offers beyond opening it, which every piece offers. */
export interface PieceActions {
  /** The status moves, main one first. */
  readonly transitions: readonly ContentTransition[]
  readonly duplicate: boolean
  readonly edit: boolean
  readonly remove: boolean
}

const NO_ACTIONS: PieceActions = { transitions: [], duplicate: false, edit: false, remove: false }

/** The actions a piece offers: an article only opens and deletes, a post Raldo is writing only opens. */
export function pieceActionsFor(subject: PieceActionSubject): PieceActions {
  const removable = subject.status === 'draft' || subject.status === 'published'
  if (subject.shape === 'article') return { ...NO_ACTIONS, remove: removable }
  if (subject.writing) return NO_ACTIONS
  return {
    transitions: STATUS_TRANSITIONS[subject.status],
    duplicate: subject.status === 'published',
    edit: subject.status !== 'published',
    remove: removable,
  }
}

/** The piece's actions in menu order: open, the status moves, duplicate, then delete last; edit opens the same page as open, so the menu leaves it out. */
export function pieceMenuActions(subject: PieceActionSubject): PieceAction[] {
  const actions = pieceActionsFor(subject)
  return [
    'open',
    ...actions.transitions.map((transition) => TRANSITION_ACTION[transition]),
    ...(actions.duplicate ? (['duplicate'] as const) : []),
    ...(actions.remove ? (['remove'] as const) : []),
  ]
}

/** The i18n label key an action shows with on this piece. */
export function pieceActionLabelKey(action: PieceAction, subject: PieceActionSubject): string {
  switch (action) {
    case 'open':
      return subject.shape === 'article' ? 'content.calendar.articleOpen' : 'content.seeFullPost'
    case 'edit':
      return subject.status === 'failed' ? 'content.correct' : 'common.edit'
    case 'duplicate':
      return 'content.duplicate'
    case 'remove':
      return 'common.delete'
    case 'retryPublish':
      return TRANSITION_CONTROL.retry.labelKey
    case 'schedule':
    case 'publishNow':
    case 'reschedule':
    case 'unschedule':
      return TRANSITION_CONTROL[action].labelKey
  }
}
